import React, { useState, useEffect } from 'react';
import { Target, Search, Trophy, Save } from 'lucide-react';
import { getApparatusForEvent } from '../../utils/disciplineUtils';
import './ScoreEntryView.css';

const ScoreEntryView = ({
  currentEvent,
  currentClubs,
  clubs,
  updateScore,
  loadClubGymnasts,
  gymnasts,
  LEVEL_OPTIONS,
  FRANJA_OPTIONS,
  APPARATUS_OPTIONS,
  events = [],
  auth = null,
  selectedEvent,
  setSelectedEvent
}) => {
  const [scoreEntry, setScoreEntry] = useState({
    selectedApparatus: '',
    selectedLevel: '',
    scores: {}
  });

  const [savingStatus, setSavingStatus] = useState({});
  const [selectedClub, setSelectedClub] = useState('');

  // Filtrar eventos según el role del usuario
  const getAvailableEvents = () => {
    if (!auth || auth.role === 'admin') {
      // Los admins ven todos los eventos
      return events;
    } else if (auth.role === 'judge' && auth.user && auth.user.id) {
      // Los jueces solo ven los eventos donde están asignados
      return events.filter(event => {
        if (!event.judges) return false;
        return event.judges.some(judge => judge.id === auth.user.id);
      });
    }
    return [];
  };

  const availableEvents = getAvailableEvents();

  // Auto-seleccionar el primer evento disponible si no hay ninguno seleccionado
  useEffect(() => {
    if (availableEvents.length > 0 && !selectedEvent) {
      setSelectedEvent(availableEvents[0].id);
    }
  }, [availableEvents, selectedEvent, setSelectedEvent]);

  // Cargar atletas cuando se selecciona un club
  useEffect(() => {
    if (selectedClub && loadClubGymnasts) {
      loadClubGymnasts(selectedClub);
    }
  }, [selectedClub, loadClubGymnasts]);

  // Obtener aparatos dinámicamente basado en el evento
  const getAvailableApparatus = () => {
    if (currentEvent && currentEvent.discipline && currentEvent.category) {
      return getApparatusForEvent(currentEvent);
    }
    // Fallback a los aparatos antiguos si no hay evento
    return APPARATUS_OPTIONS.map(name => ({
      code: name.toLowerCase(),
      name: name,
      apparatus: name.toLowerCase()
    }));
  };

  const availableApparatus = getAvailableApparatus();

  // Verificar que tenemos los datos necesarios - DESPUÉS de los hooks
  if (!clubs || !Array.isArray(clubs) || !currentClubs || !Array.isArray(currentClubs)) {
    return (
      <div className="score-entry-view">
        <div className="loading-screen">
          <div style={{ textAlign: 'center' }}>
            <div className="spinner"></div>
            <p style={{ color: '#4b5563' }}>Cargando clubes y atletas...</p>
          </div>
        </div>
      </div>
    );
  }

  const handleApparatusChange = (apparatus) => {
    setScoreEntry({
      selectedApparatus: apparatus,
      selectedLevel: '',
      scores: {}
    });
  };

  const handleLevelChange = (level) => {
    setScoreEntry({
      ...scoreEntry,
      selectedLevel: level
    });
  };

  const handleBackToApparatus = () => {
    setScoreEntry({
      selectedApparatus: '',
      selectedLevel: '',
      scores: {}
    });
  };

  const getGymnastsForLevel = () => {
    if (!scoreEntry.selectedLevel) return [];
    
    // Obtener todos los gimnastas del nivel seleccionado de los datos cargados
    const allGymnasts = (gymnasts || [])
      .filter(g => g.level === scoreEntry.selectedLevel)
      .map(g => {
        const club = (Array.isArray(currentClubs) ? currentClubs : []).find(c => c.id === g.club_id);
        return {
          ...g,
          clubId: g.club_id,
          clubName: club?.name || 'Desconocido',
          clubImage: club?.image_url
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    return allGymnasts;
  };

  const handleScoreSave = async (gymnast, apparatus, score) => {
    setSavingStatus({ ...savingStatus, [gymnast.id]: 'saving' });
    
    try {
      // Actualizar el puntaje
      await updateScore(gymnast.clubId, gymnast.id, apparatus, score);
      
      // Actualizar estado local
      setScoreEntry(prev => ({
        ...prev,
        scores: {
          ...prev.scores,
          [gymnast.id]: {
            ...(prev.scores[gymnast.id] || {}),
            [apparatus]: parseFloat(score) || 0
          }
        }
      }));
      
      setSavingStatus({ ...savingStatus, [gymnast.id]: 'saved' });
      setTimeout(() => {
        setSavingStatus(prev => {
          const updated = { ...prev };
          delete updated[gymnast.id];
          return updated;
        });
      }, 2000);
    } catch (error) {
      setSavingStatus({ ...savingStatus, [gymnast.id]: 'error' });
      console.error('Error guardando puntaje:', error);
    }
  };

  const filteredGymnasts = getGymnastsForLevel();

  // Mostrar mensaje si no hay eventos disponibles
  if (!currentEvent || (auth && auth.role === 'judge' && availableEvents.length === 0)) {
    return (
      <div className="score-entry-view">
        <div className="card">
          <div className="card-header">
            <h2>Registro de Puntuaciones</h2>
          </div>
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            <p>No hay eventos disponibles para ti en este momento.</p>
            <p style={{ fontSize: '14px', marginTop: '8px' }}>
              Los administradores deberán asignarte a un evento para que puedas registrar puntuaciones.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="score-entry-view">
      {/* Event Selector para jueces */}
      {auth && auth.role === 'judge' && availableEvents.length > 1 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ padding: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              Selecciona tu evento
            </label>
            <select
              value={selectedEvent || ''}
              onChange={(e) => setSelectedEvent(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit',
                cursor: 'pointer'
              }}
            >
              <option value="">-- Selecciona un evento --</option>
              {availableEvents.map(event => (
                <option key={event.id} value={event.id}>
                  {event.name} - {event.date}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="score-entry-header">
        <div className="header-left">
          <Target size={20} color="#8b5cf6" />
          <h2>Registro de Puntuaciones</h2>
        </div>
      </div>

      {/* Aparatus Selection - First Step */}
      {!scoreEntry.selectedApparatus && (
        <div className="apparatus-selection-section">
          <div className="selection-card">
            <h3>Selecciona el Aparato</h3>
            <p>Elige el aparato para el que vas a ingresar puntuaciones</p>
            
            <div className="apparatus-grid">
              {availableApparatus.map(apparatus => (
                <button
                  key={apparatus.code}
                  onClick={() => handleApparatusChange(apparatus.code)}
                  className="apparatus-btn-large"
                >
                  <div className="apparatus-icon-large">
                    <img
                      src={`/${apparatus.apparatus.toLowerCase()}.png`}
                      alt={apparatus.name}
                      style={{ width: '64px', height: '64px', objectFit: 'contain' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <span>{apparatus.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Level Selection and Score Entry - Second Step */}
      {scoreEntry.selectedApparatus && !scoreEntry.selectedLevel && (
        <div className="level-selection-section">
          <div className="selection-card">
            <h3>Selecciona el Nivel</h3>
            <p>Elige el nivel de las atletas para {scoreEntry.selectedApparatus}</p>
            
            <div className="level-grid">
              {LEVEL_OPTIONS.map(level => (
                <button
                  key={level}
                  onClick={() => handleLevelChange(level)}
                  className="level-btn"
                >
                  {level}
                </button>
              ))}
            </div>

            <button
              onClick={handleBackToApparatus}
              className="back-btn-link"
            >
              ← Volver a Aparatos
            </button>
          </div>
        </div>
      )}

      {/* Score Entry Grid - Third Step */}
      {scoreEntry.selectedApparatus && scoreEntry.selectedLevel && (
        <div className="score-entry-section">
          <div className="entry-header">
            <div className="entry-title">
              <h3>{scoreEntry.selectedApparatus} - {scoreEntry.selectedLevel}</h3>
              <p>{filteredGymnasts.length} atletas</p>
            </div>
            <button
              onClick={handleBackToApparatus}
              className="back-btn"
            >
              ← Cambiar Aparato
            </button>
          </div>

          {filteredGymnasts.length > 0 ? (
            <div className="gymnasts-scores-grid">
              {filteredGymnasts.map((gymnast) => {
                const currentScore = (scoreEntry.scores[gymnast.id] || {})[scoreEntry.selectedApparatus] || 
                                   gymnast.scores[scoreEntry.selectedApparatus] || '';
                const status = savingStatus[gymnast.id];
                
                return (
                  <div key={gymnast.id} className="gymnast-score-card">
                    <div className="card-header">
                      {gymnast.clubImage ? (
                        <img
                          src={gymnast.clubImage}
                          alt={gymnast.clubName}
                          className="club-logo-small-entry"
                        />
                      ) : (
                        <div className="club-logo-placeholder-small">
                          <Trophy size={16} />
                        </div>
                      )}
                      <div className="gymnast-info-entry">
                        <div className="gymnast-name">{gymnast.name}</div>
                        <div className="gymnast-club">{gymnast.clubName}</div>
                      </div>
                    </div>

                    <div className="card-body">
                      <div className="score-input-group">
                        <label>Puntaje {scoreEntry.selectedApparatus}</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={currentScore}
                          onChange={(e) => {
                            const newScore = e.target.value;
                            setScoreEntry(prev => ({
                              ...prev,
                              scores: {
                                ...prev.scores,
                                [gymnast.id]: {
                                  ...(prev.scores[gymnast.id] || {}),
                                  [scoreEntry.selectedApparatus]: newScore
                                }
                              }
                            }));
                          }}
                          className="score-input-field"
                          placeholder="0.0"
                        />
                      </div>

                      <button
                        onClick={() => handleScoreSave(gymnast, scoreEntry.selectedApparatus, currentScore)}
                        disabled={status === 'saving' || !currentScore}
                        className={`save-score-btn ${status}`}
                      >
                        {status === 'saving' && '⏳ Guardando...'}
                        {status === 'saved' && '✓ Guardado'}
                        {status === 'error' && '✗ Error'}
                        {!status && <><Save size={16} /> Guardar</>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-gymnasts">
              <Search size={48} color="#d1d5db" />
              <h4>No hay atletas en este nivel</h4>
              <p>Intenta seleccionar otro nivel</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScoreEntryView;